import { hcr_updateChangedFlag } from "contexts/hierarchy/functions/hcr_updateChangedFlag";
import { hcr_buildParentTemplates } from "contexts/hierarchy/hcr_buildParentTemplates";
import { UPDATE_HIERARCHY } from "contexts/hierarchy/interface";
import { checkChanged } from "contexts/tlf/checkChanged";
import { isTlfReadOnly } from "contexts/tlf/isTlfReadOnly";
import { updateActivePatchErrors } from "contexts/tlf/updateActivePatchErrors";
import { cloneDeep } from "lodash";
import { applyPatch } from "rfc6902";
import { IUnifiedHistoryAction, IUnifiedState, REDO } from "./interface";
import { isTlfEditablePath } from "./isTlfEditablePath";
import { app_reduceHierarchySaveBlockers } from "./saveBlockers";
import { toEditableData } from "./toEditableData";

/**
 * Applies clamped undo or redo steps without replacing any whole state slice.
 *
 * @param state - Current unified data, persisted baselines and chronological history.
 * @param action - Unified undo or redo command.
 * @returns Patched editable data with recalculated dirty flags, or unchanged state for prohibited TLF commands.
 */
export const app_undoRedo = (
  state: IUnifiedState,
  action: IUnifiedHistoryAction,
): IUnifiedState => {
  const requested = action.steps ?? 1;
  const steps = Number.isNaN(requested) ? 0 : Math.max(0, Math.floor(requested));
  const redo = action.operation === REDO;
  const index = redo
    ? Math.min(state.undoHistory.length, state.undoIndex + steps)
    : Math.max(0, state.undoIndex - steps);
  if (index === state.undoIndex) return state;

  const data = cloneDeep(toEditableData(state));
  let tlfDataChanged = false;
  for (let cursor = state.undoIndex; cursor !== index; cursor += redo ? 1 : -1) {
    const entry = state.undoHistory[redo ? cursor : cursor - 1];
    const patch = redo ? entry.forward : entry.reverse;
    const changesTlf = patch.some((operation) => isTlfEditablePath(operation.path));
    if (
      changesTlf &&
      isTlfReadOnly(
        { ...state.tlfState, tlfData: data.tlfData },
        { ...state.hierarchyState, hierarchy: data.hierarchy },
      )
    ) {
      // Decline the complete command rather than partially undoing a protected mixed transaction.
      return state;
    }
    applyPatch(data, patch);
    if (changesTlf) tlfDataChanged = true;
  }
  const tlfState = tlfDataChanged
    ? updateActivePatchErrors({
        ...state.tlfState,
        ...data.variant,
        tlfData: data.tlfData,
        forcePushToParent: true,
      })
    : state.tlfState;
  if (tlfDataChanged) checkChanged(tlfState);
  let hierarchyState = {
    ...state.hierarchyState,
    hierarchy: data.hierarchy,
    hierarchyNodes: data.hierarchyNodes,
  };
  const hierarchy = hierarchyState.hierarchy;
  if (hierarchy) {
    hierarchyState.hierarchyNodes = hierarchyState.hierarchyNodes.map((node) =>
      node.id === hierarchy.id ? hierarchy : node,
    );
    hierarchyState = hcr_updateChangedFlag(hierarchyState, {
      operation: UPDATE_HIERARCHY,
      hierarchy: {},
    });
    hierarchyState.parentTemplates = hcr_buildParentTemplates(
      hierarchy,
      hierarchyState.hierarchyNodes,
    );
    hierarchyState.isNameChanged = hierarchy.name !== hierarchyState.savedHierarchy?.name;
  }
  const saveBlockers = app_reduceHierarchySaveBlockers(
    state.saveBlockers,
    hierarchyState,
    {
      operation: UPDATE_HIERARCHY,
      hierarchy: {},
    },
    state.allowedReportTypes,
  );
  return { ...state, tlfState, hierarchyState, saveBlockers, undoIndex: index };
};
